/**
 * Touch recorder (Stage 1B / §3.1)
 *
 * RECORDS — does not attribute. Keeps an append-only, time-bounded log of
 * marketing touches (UTMs + fbclid/gclid/fbc/fbp), posts it to the events
 * service /collect keyed by a stable first-party touch_id, and writes only the
 * touch_id + latest click IDs to cart attributes so they reach the order.
 *
 * The attribution model (first/last/linear/…) lives SERVER-SIDE in the
 * warehouse, computed from this log. The browser's only job is to record
 * faithfully. Consent-gated via Shopify's Customer Privacy API.
 *
 * Replaces the old sticky single-slot localStorage attribution model.
 */

(function () {
  'use strict';

  // events service base. Set window.PEMMICAN_EVENTS_URL in theme.liquid after
  // deploy, or hardcode the Cloud Run URL here.
  var EVENTS_BASE = window.PEMMICAN_EVENTS_URL || '';
  var COLLECT_ENDPOINT = EVENTS_BASE ? EVENTS_BASE.replace(/\/$/, '') + '/collect' : '';

  var UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
  // Meta/Google append these alongside the UTMs (Meta: ad_id/adset_id/campaign_id
  // from the {{ad.id}} macros). They name the exact ad and ad set on the order.
  var AD_PARAMS = ['ad_id', 'adset_id', 'campaign_id'];
  var LOG_KEY = 'pemmican_touches';
  var TOUCH_ID_KEY = 'pemmican_touch_id';
  // Pre-consent landing buffer (sessionStorage). Lets us recover the fbclid/UTM
  // from the landing URL if the visitor only accepts the consent banner a page
  // or two later — by then the URL no longer has the params. Stored locally,
  // NEVER sent, until consent is granted; cleared at session end.
  // ponytail: a small per-session local buffer of landing params only; if your
  // privacy stance forbids any pre-consent device storage, set RECOVER=false.
  var PENDING_KEY = 'pemmican_pending_touch';
  var RECOVER = true;
  var WINDOW_DAYS = 365;         // explicit, identical in every browser
  var MAX_TOUCHES = 25;          // local buffer only — /collect merges server-side,
                                 // so the warehouse keeps the full journey
  var COOKIE_TOUCHES = 5;        // cookie mirror stays well under the 4KB limit
  var WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

  // ---- storage helpers (localStorage primary, cookie = Safari ITP bridge) ----
  function lsGet(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (e) {} }
  function getCookie(name) {
    try {
      var m = ('; ' + document.cookie).split('; ' + name + '=');
      if (m.length === 2) return decodeURIComponent(m.pop().split(';').shift());
    } catch (e) {}
    return null;
  }
  function setCookie(name, value, days) {
    try {
      var exp = new Date(Date.now() + days * 864e5);
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + exp.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) {}
  }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) {} }
  function ssDel(k) { try { sessionStorage.removeItem(k); } catch (e) {} }

  // ---- consent (UK GDPR/PECR) — fail closed ----
  function consentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return false;
      // We forward to Meta, so require marketing consent.
      if (typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
      if (typeof cp.userCanBeTracked === 'function') return cp.userCanBeTracked();
      return false;
    } catch (e) { return false; }
  }

  // ---- stable first-party touch id ----
  function getTouchId() {
    var id = lsGet(TOUCH_ID_KEY) || getCookie(TOUCH_ID_KEY);
    if (!id) {
      id = 'tch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
      lsSet(TOUCH_ID_KEY, id);
      setCookie(TOUCH_ID_KEY, id, WINDOW_DAYS);
    }
    return id;
  }

  // ---- the log: read (expiring old touches), write (capped) ----
  function readLog() {
    var raw = lsGet(LOG_KEY) || getCookie(LOG_KEY);
    var arr = [];
    if (raw) { try { arr = JSON.parse(raw) || []; } catch (e) { arr = []; } }
    var cutoff = Date.now() - WINDOW_MS;
    return arr.filter(function (t) { return t && t.ts && t.ts >= cutoff; });
  }
  function writeLog(arr) {
    var capped = arr.slice(-MAX_TOUCHES);
    lsSet(LOG_KEY, JSON.stringify(capped));
    // Cookie is only the Safari-ITP bridge: mirror the newest few, because a
    // cookie over ~4KB is silently dropped by the browser.
    setCookie(LOG_KEY, JSON.stringify(capped.slice(-COOKIE_TOUCHES)), WINDOW_DAYS);
    return capped;
  }

  // ---- build the current touch from the URL/referrer ----
  function externalReferrer() {
    try {
      if (!document.referrer) return '';
      var h = new URL(document.referrer).hostname;
      return h === window.location.hostname ? '' : document.referrer;
    } catch (e) { return ''; }
  }
  function currentTouch() {
    var p = new URLSearchParams(window.location.search);
    var t = { ts: Date.now(), landing: window.location.pathname + window.location.search, referrer: externalReferrer() };
    UTM_PARAMS.forEach(function (k) { var v = p.get(k); if (v) t[k] = v; });
    AD_PARAMS.forEach(function (k) { var v = p.get(k); if (v) t[k] = v; });
    var fbclid = p.get('fbclid');
    var gclid = p.get('gclid');
    if (fbclid) {
      t.fbclid = fbclid;
      // fbc format per Meta: fb.1.<ts>.<fbclid>. Only when a real fbclid exists.
      t.fbc = 'fb.1.' + Date.now() + '.' + fbclid;
    }
    if (gclid) t.gclid = gclid;
    var fbp = getCookie('_fbp');       // read-only — never fabricate
    if (fbp) t.fbp = fbp;
    return t;
  }
  function isTouch(t) {
    return !!(t.referrer || t.utm_source || t.utm_medium || t.utm_campaign ||
      t.utm_content || t.utm_term || t.utm_id || t.fbclid || t.gclid ||
      t.ad_id || t.adset_id || t.campaign_id);
  }
  function sameTouch(a, b) {
    if (!a || !b) return false;
    var keys = UTM_PARAMS.concat(AD_PARAMS, ['fbclid', 'gclid', 'referrer']);
    return keys.every(function (k) { return (a[k] || '') === (b[k] || ''); });
  }

  // ---- post the log to /collect ----
  function postCollect(touchId, touches) {
    if (!COLLECT_ENDPOINT) return;  // not configured yet — touches still in cart attrs
    try {
      fetch(COLLECT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ touch_id: touchId, consent: true, touches: touches }),
        keepalive: true,
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- write touch_id + latest click IDs to cart attributes ----
  function latestClickIds(touches) {
    var out = {};
    for (var i = touches.length - 1; i >= 0; i--) {
      ['fbclid', 'fbc', 'fbp', 'gclid'].forEach(function (k) {
        if (!out[k] && touches[i][k]) out[k] = touches[i][k];
      });
    }
    return out;
  }
  // The last touch that came from a campaign, in full: source/medium/campaign
  // plus the ad-level fields (utm_content/term/id + Meta's ad_id/adset_id/
  // campaign_id), so the order says which ad set and which ad it came from.
  function lastUtm(touches) {
    for (var i = touches.length - 1; i >= 0; i--) {
      var t = touches[i];
      if (t.utm_source || t.utm_campaign || t.ad_id) {
        var out = {};
        UTM_PARAMS.concat(AD_PARAMS).forEach(function (k) { if (t[k]) out[k] = t[k]; });
        return out;
      }
    }
    return {};
  }
  function writeCartAttributes(touchId, touches) {
    var attrs = { pemmican_touch_id: touchId };
    var ids = latestClickIds(touches);
    Object.keys(ids).forEach(function (k) { attrs[k] = ids[k]; });
    var u = lastUtm(touches);
    Object.keys(u).forEach(function (k) { if (u[k]) attrs[k] = u[k]; });
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      }).catch(function () {});
    } catch (e) {}
  }

  // ---- pre-consent landing buffer (recovery) ----
  function readPending() {
    var raw = ssGet(PENDING_KEY);
    if (!raw) return [];
    try { return JSON.parse(raw) || []; } catch (e) { return []; }
  }
  // Capture the current landing's marketing params locally (no network) so a
  // late banner-accept can still attribute it. Only stores real touches —
  // direct visits write nothing.
  function stashPending() {
    if (!RECOVER) return;
    var t = currentTouch();
    if (!isTouch(t)) return;
    var pend = readPending();
    if (!sameTouch(t, pend[pend.length - 1])) {
      pend.push(t);
      ssSet(PENDING_KEY, JSON.stringify(pend.slice(-MAX_TOUCHES)));
    }
  }
  function drainPending(log) {
    readPending().forEach(function (p) {
      if (!sameTouch(p, log[log.length - 1])) log.push(p);
    });
    return log;
  }

  // ---- main: record this pageview's touch if consented, then sync ----
  function record() {
    if (!consentGranted()) return;  // nothing transmitted without consent

    var log = drainPending(readLog());  // fold in any pre-consent landings
    var t = currentTouch();
    if (isTouch(t) && !sameTouch(t, log[log.length - 1])) log.push(t);
    log = writeLog(log);
    ssDel(PENDING_KEY);  // recovered — clear the buffer

    var touchId = getTouchId();
    postCollect(touchId, log);
    writeCartAttributes(touchId, log);
  }

  // Runs on every page: stash the landing locally (pre-consent safe), then post
  // if consent is already granted.
  function onPageView() {
    stashPending();
    record();
  }

  function start() {
    onPageView();
    // Re-run once consent is collected (banner accepted after load) — this is
    // where the recovered landing finally gets sent.
    document.addEventListener('visitorConsentCollected', record);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // debug surface
  window.PemmicanTouch = { getTouchId: getTouchId, readLog: readLog, consentGranted: consentGranted, record: record, onPageView: onPageView };
})();
